import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { Patient } from "common/models/types";
import PatientItem from "components/molecules/PatientItem";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Spinner from "components/atoms/Spinner";
import Icon from "components/atoms/Icon";
import Input from "components/atoms/Input";
import AddPatientModal from "components/molecules/AddPatientModal";

interface PatientListProps {
  patients: Patient[];
  selectedPatient: Patient | null;
  onSelectPatient: (patient: Patient) => void;
  onCreatePatient: (patientData: {
    birthYear: string;
    gender: string;
    memo: string;
  }) => void;
  onSearch: (search?: string) => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[10]};
  border-radius: 8px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
`;

const SearchContainer = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
`;

const PatientListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const LoadMoreButton = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
  cursor: pointer;
  color: ${COLOR_SYSTEM.get("Blue")?.[500]};

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  text-align: center;
`;

const PatientList: React.FC<PatientListProps> = ({
  patients,
  selectedPatient,
  onSelectPatient,
  onCreatePatient,
  onSearch,
  hasMore,
  isLoadingMore,
  onLoadMore,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // 검색어 입력 핸들러
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // 검색 버튼 핸들러
  const handleSearch = () => {
    onSearch(searchTerm);
  };

  // 검색어 입력 후 엔터키 핸들러
  const handleEnter = (e: any) => {
    handleSearch();
  };

  // 모달 열기
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  // 모달 닫기
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // 환자 생성 핸들러
  const handleCreatePatient = (patientData: {
    birthYear: string;
    gender: string;
    memo: string;
  }) => {
    onCreatePatient(patientData);
    setIsModalOpen(false);
  };

  // 검색 버튼 컴포넌트
  const searchButton = (
    <Button
      buttonType="button"
      color="Primary"
      size="Small"
      type="Icon"
      variant="Ghost"
      onClick={handleSearch}
    >
      <Icon name="search" size={16} />
    </Button>
  );

  // 무한 스크롤 설정
  useEffect(() => {
    if (isLoadingMore) return;

    if (observer.current) {
      observer.current.disconnect();
    }

    const handleObserver = (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore) {
        onLoadMore();
      }
    };

    observer.current = new IntersectionObserver(handleObserver, {
      rootMargin: "0px 0px 100px 0px",
    });

    if (loadMoreRef.current && hasMore) {
      observer.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [hasMore, isLoadingMore, onLoadMore, patients]);

  return (
    <Container>
      <Header>
        <Text textType="H4">환자 목록</Text>
        <Button
          buttonType="button"
          color="Primary"
          size="Small"
          type="Text"
          variant="Contained"
          onClick={handleOpenModal}
        >
          환자 추가
        </Button>
      </Header>

      <SearchContainer>
        <Input
          type="text"
          placeholder="환자 이름 또는 진단명 검색"
          value={searchTerm}
          onChange={handleSearchChange}
          onEnter={handleEnter}
          rightItems={[searchButton]}
        />
      </SearchContainer>

      <PatientListContainer>
        {patients.length > 0 ? (
          <>
            {patients.map((patient) => (
              <PatientItem
                key={patient.id}
                patient={patient}
                isSelected={selectedPatient?.id === patient.id}
                onClick={() => onSelectPatient(patient)}
              />
            ))}

            {hasMore && (
              <LoadMoreButton ref={loadMoreRef} onClick={onLoadMore}>
                {isLoadingMore ? <Spinner size="Small" /> : "더 보기"}
              </LoadMoreButton>
            )}
          </>
        ) : (
          <EmptyState>
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[500]}>
              환자 정보가 없습니다
            </Text>
            <Button
              buttonType="button"
              color="Primary"
              size="Medium"
              type="Text"
              variant="Contained"
              onClick={handleOpenModal}
              style={{ marginTop: "16px" }}
            >
              환자 추가하기
            </Button>
          </EmptyState>
        )}
      </PatientListContainer>

      {isModalOpen && (
        <AddPatientModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleCreatePatient}
        />
      )}
    </Container>
  );
};

export default PatientList;
